import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { formatMoney } from '@/components/common'
import { portalApi, type PortalHallDay, type PortalSlot } from './portalApi'

/** Today, in the yyyy-mm-dd a date input wants. */
export function today(): string {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

/**
 * Which sittings are free on a day, as a customer is allowed to see it.
 *
 * <p>A taken sitting says only that it is taken. Whose booking holds it is the
 * caterer's business and another customer's privacy, and the server does not
 * send it.</p>
 *
 * <p>Clicking a free sitting selects it when {@code onPick} is given, which is
 * how the planner turns "the evening of the twelfth" into a quotation.</p>
 */
export function AvailabilityBoard({
  date,
  serviceType,
  onDateChange,
  selectedSlotId,
  onPick,
}: {
  date: string
  serviceType: string
  onDateChange?: (date: string) => void
  selectedSlotId?: string | null
  onPick?: (hall: PortalHallDay, slot: PortalSlot) => void
}) {
  const board = useQuery({
    queryKey: ['portal', 'availability', date, serviceType],
    queryFn: () =>
      portalApi.get<PortalHallDay[]>(`/availability?date=${date}&serviceType=${serviceType}`),
    enabled: Boolean(date) && Boolean(serviceType),
  })

  return (
    <Box>
      {onDateChange ? (
        <TextField
          type="date"
          size="small"
          label="Date"
          value={date}
          onChange={(event) => onDateChange(event.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: today() }}
          sx={{ mb: 2 }}
        />
      ) : null}

      {board.isLoading ? <Typography variant="body2">Checking the diary…</Typography> : null}
      {board.error ? (
        <Alert severity="error">We could not check that date just now.</Alert>
      ) : null}

      {board.data?.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing of ours is booked for this — we come to you, so any date can be
          arranged.
        </Typography>
      ) : null}

      <Stack spacing={2}>
        {(board.data ?? []).map((hall) => (
          <Card key={hall.hallId} variant="outlined">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                flexWrap="wrap"
              >
                <Typography variant="subtitle1">{hall.hallName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {hall.outletName ? `${hall.outletName} · ` : ''}seats {hall.seatingCapacity}
                </Typography>
              </Stack>

              {hall.slots.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No fixed sessions here — ask the office about timings.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {hall.slots.map((slot) => (
                    <Chip
                      key={slot.slotId}
                      label={`${slot.name} · ${slot.startTime?.slice(0, 5)}–${slot.endTime?.slice(0, 5)}${
                        slot.available && slot.rentalCharge
                          ? ` · ${formatMoney(slot.rentalCharge)}`
                          : ''
                      }`}
                      color={
                        selectedSlotId === slot.slotId
                          ? 'primary'
                          : slot.available
                            ? 'success'
                            : 'default'
                      }
                      variant={
                        selectedSlotId === slot.slotId || !slot.available ? 'filled' : 'outlined'
                      }
                      // A taken sitting is not a button. Nothing here explains
                      // who has it, because that is not ours to tell.
                      disabled={!slot.available}
                      onClick={
                        onPick && slot.available ? () => onPick(hall, slot) : undefined
                      }
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  )
}
